import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

import {
  IFreeBusySlot,
  useGetMeetingRequestFreeBusy,
} from '../../hooks/reactQuerys/MeetingRequestQueries';

interface IProps {
  idMeetingType: number;
  idLocation?: number | null;
  idRoom?: number | null;
  idUserCalendarConnection?: number | null;
  durationInMin?: number | null;
  /** Aktuell gewählter Slot-Start ("YYYY-MM-DD HH:MM:SS") oder null. */
  value: string | null;
  onChange: (start: string | null) => void;
  /** Größe des angezeigten Zeitfensters in Tagen (Navigation in diesen Schritten). */
  windowDays?: number;
}

const pad = (n: number): string => String(n).padStart(2, '0');
const toDateStr = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * Auswahl eines FREIEN Termin-Slots für den Gegenvorschlag. Lädt die freien
 * Slots aus demselben Free/Busy wie die öffentliche Buchung (raum-/
 * kalendergenau, falls Raum bzw. Kalenderverbindung bekannt) — eine hier
 * gewählte Zeit ist garantiert verfügbar (kein 409). Zeitfenster navigierbar.
 */
export const CounterProposalSlotPicker: React.FC<IProps> = ({
  idMeetingType,
  idLocation,
  idRoom,
  idUserCalendarConnection,
  durationInMin,
  value,
  onChange,
  windowDays = 14,
}) => {
  // Verschiebung des Zeitfensters in Vielfachen von windowDays (0 = ab heute).
  const [windowOffset, setWindowOffset] = useState(0);

  const { dateFrom, dateTo, windowStart, windowEnd } = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() + windowOffset * windowDays);
    const end = new Date(start);
    end.setDate(end.getDate() + windowDays);
    return {
      dateFrom: toDateStr(start),
      dateTo: toDateStr(end),
      windowStart: start,
      windowEnd: end,
    };
  }, [windowOffset, windowDays]);

  const { freeBusySlots, isLoadingFreeBusy } = useGetMeetingRequestFreeBusy(
    idMeetingType,
    dateFrom,
    dateTo,
    idLocation,
    durationInMin,
    idRoom,
    idUserCalendarConnection
  );

  const slotsByDay = useMemo(() => {
    const map = new Map<string, IFreeBusySlot[]>();
    for (const slot of freeBusySlots) {
      const day = slot.start.slice(0, 10);
      const list = map.get(day) ?? [];
      list.push(slot);
      map.set(day, list);
    }
    return map;
  }, [freeBusySlots]);

  const days = useMemo(
    () => Array.from(slotsByDay.keys()).sort(),
    [slotsByDay]
  );

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  useEffect(() => {
    if (days.length === 0) {
      setSelectedDay(null);
      return;
    }
    setSelectedDay(prev => {
      if (prev && days.includes(prev)) {
        return prev;
      }
      if (value && days.includes(value.slice(0, 10))) {
        return value.slice(0, 10);
      }
      return days[0];
    });
  }, [days, value]);

  const fmtRange = (from: Date, to: Date): string => {
    const opts: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
    };
    return `${from.toLocaleDateString('de-DE', opts)} – ${to.toLocaleDateString('de-DE', opts)}`;
  };
  const fmtDay = (day: string): string =>
    new Date(`${day}T00:00:00`).toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    });
  const fmtTime = (start: string): string => start.slice(11, 16);

  const daySlots = selectedDay ? (slotsByDay.get(selectedDay) ?? []) : [];

  return (
    <Stack spacing={1}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography variant='caption' color='text.secondary'>
          Freien Termin wählen
        </Typography>
        <Stack direction='row' alignItems='center' spacing={0.5}>
          <IconButton
            size='small'
            disabled={windowOffset === 0}
            onClick={() => setWindowOffset(o => Math.max(0, o - 1))}
            aria-label='Früherer Zeitraum'
          >
            <ChevronLeft fontSize='small' />
          </IconButton>
          <Typography
            variant='caption'
            sx={{ minWidth: 110, textAlign: 'center' }}
          >
            {fmtRange(windowStart, windowEnd)}
          </Typography>
          <IconButton
            size='small'
            onClick={() => setWindowOffset(o => o + 1)}
            aria-label='Späterer Zeitraum'
          >
            <ChevronRight fontSize='small' />
          </IconButton>
        </Stack>
      </Stack>

      {isLoadingFreeBusy ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : days.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Keine freien Termine in diesem Zeitraum. Mit den Pfeilen einen anderen
          Zeitraum wählen.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
            {days.map(day => (
              <Chip
                key={day}
                label={fmtDay(day)}
                color={day === selectedDay ? 'primary' : 'default'}
                variant={day === selectedDay ? 'filled' : 'outlined'}
                onClick={() => setSelectedDay(day)}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {daySlots.map(slot => (
              <Chip
                key={slot.start}
                label={fmtTime(slot.start)}
                color={slot.start === value ? 'primary' : 'default'}
                variant={slot.start === value ? 'filled' : 'outlined'}
                onClick={() => onChange(slot.start)}
              />
            ))}
          </Box>
        </>
      )}
    </Stack>
  );
};

export default CounterProposalSlotPicker;
